import type { ConstructionCraft, IDocUploadResult, ILinkMediaFileParams, IMediaFile, MediaFileCategory, MediaFileExpandOpts } from '@condo/domain';
import { getClient } from '@/shared/api/client';

export interface MediaFilesQuery {
    estateId?: number;
    rentalContractUnitId?: number;
    rentalContractUnitIds?: number[];
    mfhId?: number;
    rentalContractId?: number;
    categories?: MediaFileCategory[];
    tags?: string[];
    renovationInspectionId?: number;
    constructionCraft?: ConstructionCraft;
    isConstruction?: boolean;
}

export interface UploadMediaFilesParams {
    files: File[];
    category: MediaFileCategory;
    estateId?: number;
    rentalContractUnitId?: number;
    mfhId?: number;
    /** Required for MFH/estate uploads; optional for Avatar */
    userId?: string;
    defectId?: number;
    tags?: string[];
    renovationInspectionId?: number;
    constructionCraft?: ConstructionCraft;
}

export interface UpdateMediaFileParams {
    tags?: string[];
    category?: MediaFileCategory;
    filename?: string;
}

export interface SearchMediaFilesQuery {
    estateId: number;
    query: string;
}

export interface DownloadMediaAsPdfParams {
    mediaFileIds: number[];
    fileName: string;
}

export interface LinkMediaFileRequestOptions {
    signal?: AbortSignal;
}

export interface MediaFilesResponse {
    categoryFacets: Record<string, number>;
    mediaFiles: IMediaFile[];
}

/**
 * `POST /media-files` returns `IDocUploadResult<IMediaFile>[]` (`{ status, file }`), same as Cockpit.
 * Normalize to bare `IMediaFile[]` for callers.
 */
function unwrapMediaUploadResults(data: unknown): IMediaFile[] {
    const rows = Array.isArray(data) ? data : data != null ? [data] : [];
    return rows
        .map(row => {
            if (row && typeof row === 'object' && 'file' in row) {
                return (row as IDocUploadResult<IMediaFile>).file;
            }
            return row as IMediaFile;
        })
        .filter((file): file is IMediaFile => !!file && typeof file === 'object');
}

/**
 * Fetch media files based on query parameters
 */
export const getMediaFiles = async (query: MediaFilesQuery, expandOpts?: MediaFileExpandOpts): Promise<MediaFilesResponse> => {
    try {
        console.log('🔍 Fetching media files with query:', query);

        const params = {
            query,
            expand: expandOpts,
        };
        const { data } = await getClient('basecamp').get('/media-files', { params });
        console.log('✅ Media files fetched successfully:', data?.mediaFiles?.length || 0);
        return data || { categoryFacets: {}, mediaFiles: [] };
    } catch (error) {
        console.error('❌ Error fetching media files:', error);
        throw error;
    }
};

/**
 * Upload a single media file
 */
const uploadSingleMediaFile = async (file: File, params: Omit<UploadMediaFilesParams, 'files'>): Promise<IMediaFile[]> => {
    const formData = new FormData();
    formData.append('files', file);

    // Build query parameters
    const queryParams = new URLSearchParams();
    queryParams.append('category', params.category);
    if (params.estateId) {
        queryParams.append('estateId', params.estateId.toString());
    }
    if (params.rentalContractUnitId) {
        queryParams.append('rentalContractUnitId', params.rentalContractUnitId.toString());
    }
    if (params.mfhId) {
        queryParams.append('mfhId', params.mfhId.toString());
    }
    if (params.userId) {
        queryParams.append('userId', params.userId);
    }
    if (params.defectId) {
        queryParams.append('defectId', params.defectId.toString());
    }
    if (params.renovationInspectionId) {
        queryParams.append('renovationInspectionId', params.renovationInspectionId.toString());
    }
    if (params.constructionCraft) {
        queryParams.append('constructionCraft', params.constructionCraft);
    }
    if (params.tags?.length) {
        for (const tag of params.tags) {
            queryParams.append('tags', tag);
        }
    }

    const { data } = await getClient('basecamp').post(`/media-files?${queryParams.toString()}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });

    return unwrapMediaUploadResults(data);
};

/**
 * Link an uploaded GCS object as a media file (Cockpit parity: POST /media-files/link).
 * For avatars with contactId, basecamp also updates the contact and Firebase photoURL.
 */
export const linkMediaFile = async (
    params: Omit<ILinkMediaFileParams, 'estateId'> & { estateId?: number },
    options?: LinkMediaFileRequestOptions,
): Promise<IDocUploadResult<IMediaFile>> =>
    getClient('basecamp')
        .post('/media-files/link', params, { signal: options?.signal })
        .then(response => response.data);

/**
 * Upload media files sequentially to avoid race conditions
 * Follows the pattern from Cockpit's mediaFileUpload
 */
export const uploadMediaFiles = async (params: UploadMediaFilesParams): Promise<IMediaFile[]> => {
    try {
        console.log('📤 Uploading media files sequentially:', params.files.length);

        const results: IMediaFile[] = [];

        // Upload files one by one to prevent race conditions with variant generation
        for (let i = 0; i < params.files.length; i++) {
            const file = params.files[i];
            console.log(`📤 Uploading file ${i + 1}/${params.files.length}:`, file.name);

            const uploaded = await uploadSingleMediaFile(file, params);
            results.push(...uploaded);

            console.log(`✅ File ${i + 1}/${params.files.length} uploaded successfully`);
        }

        console.log('✅ All media files uploaded successfully:', results.length);
        return results;
    } catch (error) {
        console.error('❌ Error uploading media files:', error);
        throw error;
    }
};

/**
 * Update a media file
 */
export const updateMediaFile = async (mediaFileId: number, params: UpdateMediaFileParams): Promise<IMediaFile> => {
    try {
        console.log('✏️ Updating media file:', mediaFileId, params);

        const { data } = await getClient('basecamp').patch(`/media-files/${mediaFileId}`, params);

        console.log('✅ Media file updated successfully');
        return data;
    } catch (error) {
        console.error('❌ Error updating media file:', error);
        throw error;
    }
};

/**
 * Update multiple media files (bulk operation for tags/category)
 */
export const updateMediaFiles = async (mediaFileIds: number[], params: UpdateMediaFileParams): Promise<void> => {
    try {
        console.log('✏️ Bulk updating media files:', mediaFileIds.length, params);

        // Use the appropriate endpoint based on what's being updated
        if (params.tags !== undefined) {
            await getClient('basecamp').put('/media-files/tags', {
                mediaFileIds,
                tags: params.tags,
            });
        }

        if (params.category !== undefined) {
            await getClient('basecamp').put('/media-files/category', {
                mediaFileIds,
                category: params.category,
            });
        }

        console.log('✅ Media files updated successfully');
    } catch (error) {
        console.error('❌ Error bulk updating media files:', error);
        throw error;
    }
};

/**
 * Delete media files
 */
export const deleteMediaFiles = async (mediaFileIds: number[]): Promise<void> => {
    try {
        console.log('🗑️ Deleting media files:', mediaFileIds.length);

        await getClient('basecamp').delete('/media-files', {
            params: { mediaFileIds },
        });

        console.log('✅ Media files deleted successfully');
    } catch (error) {
        console.error('❌ Error deleting media files:', error);
        throw error;
    }
};

/**
 * Search media files by text query (AI-powered)
 */
export const searchMediaFilesByText = async (params: SearchMediaFilesQuery, expandOpts?: MediaFileExpandOpts): Promise<IMediaFile[]> => {
    try {
        const { data } = await getClient('basecamp').post(`/media-files/${params.estateId}/by-query`, {
            query: params.query,
            expandOpts,
        });

        console.log('✅ Media files search completed:', data?.length || 0);
        return data || [];
    } catch (error) {
        console.error('❌ Error searching media files:', error);
        throw error;
    }
};

/**
 * Download media files as PDF
 */
export const downloadMediaAsPdf = async (params: DownloadMediaAsPdfParams): Promise<Blob> => {
    try {
        console.log('📄 Downloading media as PDF:', params);

        const { data } = await getClient('basecamp').post('/media-files/download-pdf', params, {
            responseType: 'blob',
        });

        console.log('✅ PDF generated successfully');
        return data;
    } catch (error) {
        console.error('❌ Error downloading PDF:', error);
        throw error;
    }
};
